import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatDate } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const useRiskSignalsFunctions = () => {
  // Filters
  const [severity, setSeverity] = useState('all');
  const [status, setStatus] = useState('active');
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // X-02: user-controllable page size via PageSizeSelector.
  const [PAGE_SIZE, setPageSize] = useState(20);

  // Data
  const [signals, setSignals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);

  // Modals
  const [dismissTarget, setDismissTarget] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Bulk selection
  const [selected, setSelected] = useState(new Set());

  const fetchSignals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (severity !== 'all') params.set('severity', severity);
      if (status !== 'all') params.set('status', status);
      if (type !== 'all') params.set('type', type);
      // search is client-side filtered from fallback
      const resp = await axios.get(`${BASE_URL}/risk-signals?${params}`, { withCredentials: true });
      if (resp.data?.data) {
        setSignals(resp.data.data.signals || []);
        setTotal(resp.data.data.total || 0);
      }
    } catch {
      /* empty state on error */
    } finally {
      setLoading(false);
    }
  }, [severity, status, type, page, PAGE_SIZE]);

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/risk-signals/config`, { withCredentials: true });
      if (resp.data?.data?.config) setConfig(resp.data.data.config);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchSignals();
  }, [fetchSignals]);
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleAcknowledge = async id => {
    setActionLoading(id);
    try {
      await axios.patch(`${BASE_URL}/risk-signals/${id}/acknowledge`, {}, { withCredentials: true });
      await fetchSignals();
    } catch {
      /* silent */
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolve = async id => {
    setActionLoading(id);
    try {
      await axios.patch(`${BASE_URL}/risk-signals/${id}/resolve`, {}, { withCredentials: true });
      await fetchSignals();
    } catch {
      /* silent */
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismissConfirm = async reason => {
    if (!dismissTarget) return;
    setActionLoading(dismissTarget._id);
    try {
      await axios.patch(`${BASE_URL}/risk-signals/${dismissTarget._id}/dismiss`, { reason }, { withCredentials: true });
      setDismissTarget(null);
      await fetchSignals();
    } catch {
      /* silent */
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveConfig = async draft => {
    try {
      await axios.patch(`${BASE_URL}/risk-signals/config`, { signals: draft.signals, slaRules: draft.slaRules }, { withCredentials: true });
      setConfig(draft);
      setShowConfig(false);
    } catch {
      /* silent */
    }
  };

  // Bulk acknowledge
  const handleBulkAcknowledge = async () => {
    for (const id of selected) {
      try {
        await axios.patch(`${BASE_URL}/risk-signals/${id}/acknowledge`, {}, { withCredentials: true });
      } catch {}
    }
    setSelected(new Set());
    fetchSignals();
  };

  // Client-side search filter
  const filteredSignals = search.trim()
    ? signals.filter(s => {
        const q = search.toLowerCase();
        return s.claim?.claimNumber?.toLowerCase().includes(q) || s.claim?.claimant?.name?.toLowerCase().includes(q) || s.title?.toLowerCase().includes(q);
      })
    : signals;

  const fmtDate = d => (d ? formatDate(d) : '—');

  const pages = Math.ceil(total / PAGE_SIZE);
  const toggleSelect = id => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };
  const toggleAll = () => {
    if (selected.size === filteredSignals.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredSignals.map(s => s._id)));
    }
  };

  return {
    severity,
    setSeverity,
    status,
    setStatus,
    type,
    setType,
    search,
    setSearch,
    page,
    setPage,
    total,
    loading,
    config,
    dismissTarget,
    setDismissTarget,
    showConfig,
    setShowConfig,
    actionLoading,
    selected,
    fetchSignals,
    handleAcknowledge,
    handleResolve,
    handleDismissConfirm,
    handleSaveConfig,
    handleBulkAcknowledge,
    fmtDate,
    pages,
    toggleSelect,
    toggleAll,
    filteredSignals,
    PAGE_SIZE,
    setPageSize: (n) => { setPageSize(n); setPage(1); },
  };
};

export default useRiskSignalsFunctions;
